import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useBackTarget } from "@/lib/back-nav";
import { ContentStatus, StudioContentStatus } from "@/lib/schema";
import type { ContentTypeConfig } from "@/lib/studio-constants";
import { STATUS_CONFIG } from "@/lib/studio-constants";
import {
  AlertTriangle,
  ArrowLeft,
  Copy,
  Loader2,
  Save,
  Send,
  Undo2,
} from "lucide-react";
import { type ReactNode } from "react";
import { Link } from "wouter";

interface BuilderShellProps {
  contentTypeConfig: ContentTypeConfig;
  mode: "create" | "edit";
  title?: string;
  version?: number;
  status?: StudioContentStatus | ContentStatus;
  isLoading?: boolean;
  notFound?: boolean;
  isReadOnly?: boolean;
  isSaving?: boolean;
  isSubmitting?: boolean;
  isWithdrawing?: boolean;
  isCreatingVersion?: boolean;
  // Ownership/role gate for "Create New Version". Omitted or false hides the
  // button: a non-owner coach must not be offered a version fork of another
  // coach's published content (the server 403s it via assertOwner regardless).
  canCreateVersion?: boolean;
  // EVO-16: MediaConvert reason shown when the version's status is "failed".
  failureReason?: string | null;
  onSave?: () => void;
  onSubmit?: () => void;
  onWithdraw?: () => void;
  onCreateVersion?: () => void;
  headerActions?: ReactNode;
  children: ReactNode;
}

export function BuilderShell({
  contentTypeConfig,
  mode,
  title,
  version,
  status,
  isLoading,
  notFound,
  isReadOnly,
  isSaving,
  isSubmitting,
  isWithdrawing,
  isCreatingVersion,
  canCreateVersion,
  failureReason,
  onSave,
  onSubmit,
  onWithdraw,
  onCreateVersion,
  headerActions,
  children,
}: BuilderShellProps) {
  const statusConfig = status ? STATUS_CONFIG[status] : null;
  const isLive = status === "published";
  const isArchived = status === "archived";
  const isFailed = status === "failed";
  const backTarget = useBackTarget(contentTypeConfig.listRoute);
  const pageTitle =
    mode === "create"
      ? `Create ${contentTypeConfig.label}`
      : title || `Edit ${contentTypeConfig.label}`;

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        data-testid="builder-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="p-6 text-center" data-testid="builder-not-found">
        <p className="text-muted-foreground">
          {contentTypeConfig.label} content not found
        </p>
        <Link href={contentTypeConfig.listRoute}>
          <Button
            variant="outline"
            className="mt-4"
            data-testid="button-back-not-found"
          >
            Back to {contentTypeConfig.pluralLabel}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div
      className="p-6 space-y-6 max-w-4xl mx-auto"
      data-testid="builder-shell"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <Link href={backTarget}>
          <Button variant="ghost" size="icon" data-testid="button-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid="text-builder-title"
          >
            {pageTitle}
          </h1>
          {mode === "edit" && (version || statusConfig) && (
            <div className="flex items-center gap-2 mt-0.5">
              {version && (
                <span
                  className="text-sm text-muted-foreground"
                  data-testid="text-version"
                >
                  v{version}
                </span>
              )}
              {statusConfig && (
                <Badge
                  variant={statusConfig.variant}
                  className="no-default-hover-elevate no-default-active-elevate"
                  data-testid="badge-status"
                >
                  {statusConfig.label}
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          {mode === "edit" && isLive && onCreateVersion && canCreateVersion ? (
            <Button
              onClick={onCreateVersion}
              disabled={isCreatingVersion}
              data-testid="button-create-version"
            >
              {isCreatingVersion ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Create New Version
            </Button>
          ) : (
            <>
              {!isReadOnly && onSave && (
                <Button
                  variant="outline"
                  onClick={onSave}
                  disabled={isSaving || isSubmitting}
                  data-testid="button-save"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Draft
                </Button>
              )}
              {!isReadOnly &&
                onSubmit &&
                (status === "draft" || status === "changes_requested") && (
                  <Button
                    onClick={onSubmit}
                    disabled={isSaving || isSubmitting}
                    className="evo-gradient text-white hover:opacity-90"
                    data-testid="button-submit"
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {status === "changes_requested"
                      ? "Resubmit for Approval"
                      : "Submit for Approval"}
                  </Button>
                )}
              {onWithdraw && (status === "submitted" || isFailed) && (
                <Button
                  variant="outline"
                  onClick={onWithdraw}
                  disabled={isWithdrawing}
                  data-testid="button-withdraw"
                >
                  {isWithdrawing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Undo2 className="h-4 w-4 mr-2" />
                  )}
                  Pull back to Draft
                </Button>
              )}
              {/* TODO: Wire Approve button here.
                  Each edit page should pass `onApprove` + `canApprove` from
                  useContentLifecycle. Render when `canApprove && status === "submitted"`.
                  Server endpoint: POST /content/review/:type/:id with action="approve".
                  Example:
                  {onApprove && canApprove && status === "submitted" && (
                    <Button onClick={onApprove} data-testid="button-approve">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  )}
              */}
            </>
          )}
        </div>
      </div>

      {isLive && (
        <Card className="border-amber-500/30 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p
                className="text-sm font-medium text-amber-800 dark:text-amber-200"
                data-testid="text-live-warning"
              >
                This content is currently live
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
                Editing published content creates a new version. The current
                live version remains unchanged until the new version is approved
                and published.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {isArchived && (
        <Card className="border-muted bg-muted/30">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <p
              className="text-sm text-muted-foreground"
              data-testid="text-archived-notice"
            >
              This content is archived and cannot be edited.
            </p>
          </CardContent>
        </Card>
      )}

      {isFailed && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <p
              className="text-sm text-destructive"
              data-testid="text-failed-notice"
            >
              Transcoding failed: {failureReason || "no reason recorded"}. Pull
              back to Draft to replace the file or retry, then resubmit for
              approval.
            </p>
          </CardContent>
        </Card>
      )}

      {children}
    </div>
  );
}
